// AddressItem.tsx
import React, { useState } from 'react';
import { Check, X, MapPin, ExternalLink, CheckCircle, XCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import type { AddressVerification } from '../../types';

const addressRejectionReasons = [
    { value: 'wrong_location', label: 'Coordinates do not match address' },
    { value: 'incomplete_address', label: 'Address is incomplete' },
    { value: 'invalid_address', label: 'Address does not exist' },
    { value: 'business_address', label: 'Appears to be a business, not residential' },
    { value: 'po_box', label: 'PO Box address (not accepted)' },
    { value: 'other', label: 'Other (specify)' }
];

interface AddressItemProps {
    item: AddressVerification;
    onVerify: (id: number, approved: boolean, reason?: string) => Promise<void>;
    showStatusBadge?: boolean;
}

const AddressItem: React.FC<AddressItemProps> = ({ item, onVerify, showStatusBadge }) => {
    const [showRejectOptions, setShowRejectOptions] = useState(false);
    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);

    const hasCoordinates = item.latitude && item.longitude;
    const lat = hasCoordinates ? Number(item.latitude) : 0;
    const lng = hasCoordinates ? Number(item.longitude) : 0;

    // Status flags
    const isPending = item.address_verification_status === 'pending' && item.latitude;
    const isNeedsLocation = item.address_verification_status === 'needs_location';
    const isApproved = item.address_verification_status === 'approved';
    const isRejected = item.address_verification_status === 'rejected';

    const googleMapsUrl = hasCoordinates
        ? `https://www.google.com/maps?q=${lat},${lng}`
        : '#';

    const handleApprove = async () => {
        setIsVerifying(true);
        try {
            await onVerify(item.id, true);
            toast.success('Address approved');
        } catch (error) {
            toast.error('Failed to approve address');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleReject = async () => {
        if (!selectedReason) {
            toast.error('Please select a reason');
            return;
        }

        const finalReason = selectedReason === 'other'
            ? `Other: ${customReason}`
            : addressRejectionReasons.find(r => r.value === selectedReason)?.label;

        setIsVerifying(true);
        try {
            await onVerify(item.id, false, finalReason);
            toast.success('Address rejected');
            setShowRejectOptions(false);
            setSelectedReason('');
            setCustomReason('');
        } catch (error) {
            toast.error('Failed to reject address');
        } finally {
            setIsVerifying(false);
        }
    };

    const renderStatusBadge = () => {
        if (!showStatusBadge) return null;

        const config = {
            pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
            needs_location: { bg: 'bg-blue-100', text: 'text-blue-800', icon: MapPin, label: 'Needs Location' },
            approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Approved' },
            rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' }
        };

        const status = item.address_verification_status;
        const style = config[status] || config.pending;
        const Icon = style.icon;

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
                <Icon size={14} className="mr-1" />
                {style.label}
            </span>
        );
    };

    return (
        <div className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
            {/* User Info Header */}
            <div className="flex items-center mb-3 pb-2 border-b">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                    <span className="text-sm font-medium text-blue-600">
                        {item.user__first_name[0]}{item.user__last_name[0]}
                    </span>
                </div>
                <div>
                    <h4 className="font-medium">{item.user__first_name} {item.user__last_name}</h4>
                    <p className="text-xs text-gray-500">{item.user__email}</p>
                </div>

                <div className="flex items-center gap-2 ml-4">
                    {renderStatusBadge()}
                </div>
            </div>

            {/* Map and Address Side by Side */}
            <div className="flex gap-4">
                {/* Left - Map */}
                <div className="w-64">
                    {hasCoordinates ? (
                        <div className="relative group">
                            <iframe
                                title="Map"
                                width="100%"
                                height="180"
                                frameBorder="0"
                                src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`}
                                className="rounded-lg border"
                            />
                        </div>
                    ) : (
                        <div className="h-44 bg-gray-100 border rounded-lg flex items-center justify-center">
                            <p className="text-xs text-gray-500">No location data</p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-1">
                        {isPending && (
                            <>
                                <button
                                    onClick={() => onVerify(item.id, true)}
                                    disabled={isVerifying}
                                    className="flex-1 px-3 py-2 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                                >
                                    Approve
                                </button>
                                <button
                                    onClick={() => onVerify(item.id, false)}
                                    disabled={isVerifying}
                                    className="flex-1 px-3 py-2 border border-red-300 text-red-700 text-xs font-medium rounded-lg hover:bg-red-50 disabled:opacity-50"
                                >
                                    Reject
                                </button>
                            </>
                        )}

                        {isNeedsLocation && (
                            <button
                                onClick={() => onVerify(item.id, true)}
                                disabled={isVerifying}
                                className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                Approve Without Location
                            </button>
                        )}

                        {isApproved && (
                            <button
                                disabled
                                className="flex-1 px-3 py-2 bg-green-100 text-green-700 text-xs font-medium rounded-lg cursor-default border border-green-200 flex items-center justify-center"
                            >
                                <CheckCircle size={14} className="mr-1" />
                                Approved
                            </button>
                        )}

                        {isRejected && (
                            <button
                                disabled
                                className="flex-1 px-3 py-2 bg-red-100 text-red-700 text-xs font-medium rounded-lg cursor-default border border-red-200 flex items-center justify-center"
                            >
                                <XCircle size={14} className="mr-1" />
                                Rejected
                            </button>
                        )}
                    </div>

                    {/* Reject Options */}
                    {showRejectOptions && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                            <select
                                value={selectedReason}
                                onChange={(e) => setSelectedReason(e.target.value)}
                                className="w-full px-2 py-1 text-xs border rounded mb-2"
                                autoFocus
                            >
                                <option value="">Select reason...</option>
                                {addressRejectionReasons.map(r => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>

                            {selectedReason === 'other' && (
                                <input
                                    type="text"
                                    value={customReason}
                                    onChange={(e) => setCustomReason(e.target.value)}
                                    placeholder="Specify reason..."
                                    className="w-full px-2 py-1 text-xs border rounded mb-2"
                                />
                            )}

                            <div className="flex gap-1">
                                <button
                                    onClick={handleReject}
                                    disabled={!selectedReason || (selectedReason === 'other' && !customReason)}
                                    className="flex-1 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                                >
                                    Confirm Reject
                                </button>
                                <button
                                    onClick={() => {
                                        setShowRejectOptions(false);
                                        setSelectedReason('');
                                        setCustomReason('');
                                    }}
                                    className="px-2 py-1 border rounded text-xs hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right - Written Address with labels */}
                <div className="flex-1">
                    <a
                        href={googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors group"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                SUBMITTED ADDRESS
                            </p>
                            {hasCoordinates && (
                                <ExternalLink size={14} className="text-gray-400 group-hover:text-blue-600" />
                            )}
                        </div>

                        <div className="space-y-1 text-sm">
                            {item.house_number && (
                                <div className="flex">
                                    <span className="w-20 text-gray-500">Number:</span>
                                    <span className="text-gray-900">{item.house_number}</span>
                                </div>
                            )}
                            <div className="flex">
                                <span className="w-20 text-gray-500">Street:</span>
                                <span className="text-gray-900">{item.address_line_1}</span>
                            </div>
                            {item.address_line_2 && (
                                <div className="flex">
                                    <span className="w-20 text-gray-500">Line 2:</span>
                                    <span className="text-gray-900">{item.address_line_2}</span>
                                </div>
                            )}
                            <div className="flex">
                                <span className="w-20 text-gray-500">City:</span>
                                <span className="text-gray-900">{item.city__name}</span>
                            </div>
                            {item.delivery_note && (
                                <div className="flex pt-2 mt-1 border-t">
                                    <span className="w-20 text-gray-500">Note:</span>
                                    <span className="text-gray-600 text-xs">{item.delivery_note}</span>
                                </div>
                            )}
                        </div>

                        {hasCoordinates && (
                            <p className="text-xs text-gray-400 mt-2 pt-1 border-t border-gray-200">
                                <MapPin size={12} className="inline mr-1" />
                                {lat.toFixed(6)}, {lng.toFixed(6)}
                            </p>
                        )}
                    </a>
                </div>
            </div>
        </div>
    );
};

export default AddressItem;